// src/components/compliance/modals/ApprovedExceptionsModal.jsx

import React from "react";
import {
  cleanValue,
  formatRelativeTime,
  formatTimestamp,
  getDisplayStateLabel,
  getTaskStatusLabel,
  normalizeText,
  buildControlLabel,
} from "../../../utils/complianceFormatters";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import ModalActionRow from "./shared/ModalActionRow";

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
};

const taskCardStyle = {
  border: "1px solid rgba(251, 191, 36, 0.26)",
  borderRadius: 16,
  padding: 16,
  background: "linear-gradient(180deg, rgba(251, 191, 36, 0.07), rgba(15, 23, 42, 0.46))",
  display: "grid",
  gap: 12,
};

const metricLabelStyle = {
  color: "#f8fafc",
  fontSize: 12,
  fontWeight: 800,
};

const metricSubStyle = {
  color: "#bfdbfe",
  fontSize: 12,
  marginTop: 4,
  lineHeight: 1.35,
};

function getLastValidation(task = {}) {
  const metadata = task?.task_metadata || task?.metadata || {};
  return metadata?.last_validation || metadata?.lastValidation || null;
}

function getReviewDate(task = {}) {
  const lastValidation = getLastValidation(task) || {};
  const metadata = lastValidation?.metadata || task?.task_metadata || task?.metadata || {};
  return cleanValue(metadata.review_date || metadata.reviewDate || task.review_date, "");
}

function getDecisionOwner(task = {}) {
  const lastValidation = getLastValidation(task) || {};
  const metadata = lastValidation?.metadata || task?.task_metadata || task?.metadata || {};
  return cleanValue(metadata.business_owner || metadata.owner || task.owner_name, "—");
}

function getDecisionPath(task = {}) {
  const lastValidation = getLastValidation(task) || {};
  const metadata = lastValidation?.metadata || task?.task_metadata || task?.metadata || {};
  return cleanValue(metadata.decision_path || metadata.governance || metadata.validation_governance, "approved exception").replace(/_/g, " ");
}

function isReviewDateSoon(reviewDate) {
  if (!reviewDate) return false;
  const parsed = new Date(reviewDate);
  if (Number.isNaN(parsed.getTime())) return false;
  const now = new Date();
  const daysUntilReview = (parsed.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return daysUntilReview >= 0 && daysUntilReview <= 30;
}

function SummaryChip({ label, tone = "default" }) {
  const color =
    tone === "danger"
      ? "#f87171"
      : tone === "warn"
      ? "#fbbf24"
      : tone === "ok"
      ? "#34d399"
      : "#94a3b8";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "4px 8px",
        fontSize: 11,
        fontWeight: 900,
        lineHeight: 1.1,
        color,
        border: `1px solid color-mix(in srgb, ${color}, transparent 62%)`,
        background: `color-mix(in srgb, ${color}, transparent 90%)`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

export default function ApprovedExceptionsModal({
  open,
  framework,
  approvedExceptionTasks = [],
  acceptedRiskFindings = 0,
  approvedExceptionControls = 0,
  onClose,
  onReviewTask,
  onViewGuidance,
  onOpenDrift,
}) {
  if (!open) return null;

  const rows = Array.isArray(approvedExceptionTasks) ? approvedExceptionTasks : [];
  const controlsAffected = new Set(
    rows
      .map((task) => cleanValue(task.control_id || task.controlId, ""))
      .filter(Boolean)
  ).size;
  const reviewSoonCount = rows.filter((task) => isReviewDateSoon(getReviewDate(task))).length;
  const evidenceLinkedCount = rows.filter((task) => {
    const metadata = task?.task_metadata || task?.metadata || {};
    const lastValidation = getLastValidation(task) || {};
    return Boolean(
      metadata?.evidence_links?.length ||
        metadata?.evidenceLinked ||
        lastValidation?.evidence_id ||
        lastValidation?.archive_id ||
        task.evidence_count
    );
  }).length;

  return (
    <ModalShell
      open={open}
      ariaLabel="Approved exceptions"
      eyebrow="OASYS · ACCEPTED RISK"
      title="Approved Exceptions"
      subtitle="Review accepted-risk decisions that are intentionally separated from active unresolved work. These items should remain evidence-backed, owner-approved, and time-bound."
      onClose={onClose}
      maxWidth="1120px"
      accentBorder="rgba(251, 191, 36, 0.45)"
    >
      <div style={metricGridStyle}>
        <MetricTile
          label="Accepted Risk"
          value={acceptedRiskFindings || rows.length}
          sub={`${framework} accepted-risk finding${acceptedRiskFindings === 1 ? "" : "s"}`}
          color="#fbbf24"
        />
        <MetricTile
          label="Controls Affected"
          value={approvedExceptionControls || controlsAffected}
          sub="governed by exception"
          color="#fbbf24"
        />
        <MetricTile
          label="Review Soon"
          value={reviewSoonCount}
          sub="review date within 30 days"
          color={reviewSoonCount > 0 ? "#f87171" : "#34d399"}
        />
        <MetricTile
          label="Evidence Linked"
          value={evidenceLinkedCount}
          sub={`of ${rows.length} tracked item${rows.length === 1 ? "" : "s"}`}
          color={evidenceLinkedCount >= rows.length && rows.length > 0 ? "#34d399" : "#fbbf24"}
        />
      </div>

      <ModalSection
        title="Approved Exception Activity"
        description="Showing remediation tasks with approved-exception governance. Use Review / Manage to maintain evidence, owners, dates, and closure notes."
        badge={`${rows.length} shown`}
        badgeTone={rows.length > 0 ? "warn" : "ok"}
      >
        {rows.length === 0 ? (
          <div className="muted" style={{ padding: 14 }}>
            No approved exceptions are currently tracked for this framework.
          </div>
        ) : (
          rows.map((task) => {
            const controlId = task.control_id || task.controlId || "";
            const ruleId = task.rule_id || task.ruleId || "";
            const resourceId = task.resource_id || task.resourceId || "";
            const reviewDate = getReviewDate(task);
            const decisionOwner = getDecisionOwner(task);
            const decisionPath = getDecisionPath(task);
            const validationStatus = task.validation_status || task.validationStatus || "approved_exception";
            const reviewSoon = isReviewDateSoon(reviewDate);

            return (
              <article key={task.id || `${controlId}-${ruleId}-${resourceId}`} style={taskCardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                      <SummaryChip label="Approved Exception" tone="warn" />
                      <SummaryChip label={reviewDate ? `Review ${reviewDate}` : "No review date"} tone={reviewSoon ? "danger" : "ok"} />
                      <SummaryChip label={getTaskStatusLabel(task.status || "open")} tone={normalizeText(task.status) === "closed" ? "ok" : "default"} />
                    </div>
                    <h4 style={{ margin: 0, color: "#f8fafc", fontSize: 17, lineHeight: 1.25 }}>
                      {buildControlLabel(controlId, ruleId)}
                    </h4>
                    <p style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.55, margin: "8px 0 0" }}>
                      This risk is retained through an approved exception. Keep the owner, evidence, review date, and closure rationale audit-ready.
                    </p>
                  </div>

                  <ModalActionRow
                    actions={[
                      {
                        key: "review",
                        label: "Review / Manage",
                        onClick: () => onReviewTask(task, { returnTo: "approved_exceptions" }),
                      },
                      {
                        key: "guidance",
                        label: "Guidance",
                        onClick: () => onViewGuidance(task, { returnTo: "approved_exceptions" }),
                      },
                      {
                        key: "drift",
                        label: "Open in Drift",
                        onClick: () =>
                          onOpenDrift({
                            framework: task.framework || framework,
                            controlId: controlId || undefined,
                            ruleId: ruleId || undefined,
                            resourceId: resourceId || undefined,
                          }),
                      },
                    ]}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }}>
                  <div>
                    <div style={metricLabelStyle}>Owner</div>
                    <div style={metricSubStyle}>{decisionOwner}</div>
                  </div>
                  <div>
                    <div style={metricLabelStyle}>Decision Path</div>
                    <div style={metricSubStyle}>{decisionPath}</div>
                  </div>
                  <div>
                    <div style={metricLabelStyle}>Validation</div>
                    <div style={metricSubStyle}>{getDisplayStateLabel(validationStatus, "approved exception")}</div>
                  </div>
                  <div>
                    <div style={metricLabelStyle}>Updated</div>
                    <div style={metricSubStyle} title={formatTimestamp(task.updated_at || task.validated_at || task.created_at)}>
                      {formatRelativeTime(task.updated_at || task.validated_at || task.created_at)}
                    </div>
                  </div>
                </div>

                {resourceId ? (
                  <p style={{ color: "#bfdbfe", fontSize: 12, lineHeight: 1.5, margin: 0 }}>
                    <strong style={{ color: "#f8fafc" }}>Resource:</strong> {resourceId}
                  </p>
                ) : null}
              </article>
            );
          })
        )}
      </ModalSection>
    </ModalShell>
  );
}
